import React, { useEffect, useState } from "react";

import { useMsal } from "@azure/msal-react";
import {
    IActivityManagementWorkflow,
    IPostProcessWorkflow,
} from "../../../../../../context/interfaces/IWorkflowContext";
import ActivityManagementWorkflowService from "../../../../../../service/Docente/ActivityManagementWorkflowService";
import { IContent } from "../../../../interface/IActivitiesManagementModal";
import { ISummary } from "../../../../interface/IActivitiesManagementSummary";
import DialogStyles from "../../../../../../styles/components/DialogStyles";
import ButtonStyles from "../../../../../../styles/components/ButtonStyles";
import { StyledFormCheck } from "../../../../../../styles/MainStyles";
import SummaryStyles from "../../../../../../styles/pages/Workflows/ActivitiesManagementSummaryStyles";
import InputTextArea from "../../../../../../components/InputTextArea";
import SummaryList from "./SummaryList";
import { useWorkflow } from "../../../../../../context/WorkflowContext";
import GenericUtils from "../../../../../../utils/GenericUtils";
import DialogMessage from "../../../../../../components/DialogMessage";
import DialogMessageProps from "../../../../../../model/DialogMessageProps";

interface IModalProps {
    workflow: IActivityManagementWorkflow | null;
    handleChangeContent: (content: IContent) => void;
    onClose: (callback?: any) => void;
    readOnly: boolean;
}

interface IFormSubmit {
    isChecked: boolean;
    justification: string;
}

const { StyledContainerButton } = DialogStyles;
const { BtnPrimary } = ButtonStyles;

const ActivitiesManagementSummary: React.FC<IModalProps> = ({
    workflow,
    handleChangeContent,
    onClose,
    readOnly = false,
}) => {
    const { instance } = useMsal();

    const [summary, setSummary] = useState<ISummary>({} as ISummary);
    const [formSubmit, setFormSubmit] = useState<IFormSubmit>({
        isChecked: false,
        justification: "",
    });
    const [dialogMessageProps, setDialogMessageProps] =
        useState<DialogMessageProps>({} as DialogMessageProps);
    const workflowContext = useWorkflow();
    const { workflowTypeId } = workflowContext.workflowFilter;
    const { processoId } = workflow as IActivityManagementWorkflow;

    const handleCloseDialogMessage = () => {
        setDialogMessageProps({} as DialogMessageProps);
    };

    const handleSummary = async () => {
        const response =
            await ActivityManagementWorkflowService.getExtractHoursWorkflow(
                workflowTypeId,
                processoId,
                instance
            );

        if (GenericUtils.isSuccessStatus(response.status)) {
            setSummary(response.data);
        } else {
            const dialogError = {
                ...GenericUtils.getPropsDialogError(response),
                onClose: handleCloseDialogMessage,
            };
            setDialogMessageProps(dialogError);
        }
    };

    const handleChangeCheck = () => {
        setFormSubmit({
            ...formSubmit,
            isChecked: !formSubmit.isChecked,
        });
    };

    const handleChangeJustification = (value: string) => {
        setFormSubmit({
            ...formSubmit,
            justification: value,
        });
    };

    const handleLabelCheckbox = () => {
        if (summary.resultadoFinal?.saldoFinalHoras > 0)
            return "Estou ciente das horas positivas trabalhadas (Crédito)";

        if (summary.resultadoFinal?.saldoFinalHoras < 0)
            return "Estou ciente das horas não realizadas (Débito)";

        return "";
    };

    const isEnabledSubmit = (): boolean => {
        if (summary.resultadoFinal?.saldoFinalHoras !== 0)
            return formSubmit.isChecked && formSubmit.justification.length > 0;

        return true;
    };

    const handleSubmit = async () => {
        if (isEnabledSubmit()) {
            const payload: IPostProcessWorkflow = {
                processoId,
                resultadoFinal: {
                    ...summary.resultadoFinal,
                },
                justificativaProcesso: formSubmit.justification,
                saldoFinalDiferenteZero:
                    summary.resultadoFinal?.saldoFinalHoras !== 0,
            };

            const postResponse =
                await ActivityManagementWorkflowService.endProcessWorkflow(
                    workflowTypeId,
                    payload,
                    instance
                );

            if (GenericUtils.isSuccessStatus(postResponse.status)) {
                onClose();
            } else {
                const dialogError = {
                    ...GenericUtils.getPropsDialogError(postResponse),
                    onClose: handleCloseDialogMessage,
                };
                setDialogMessageProps(dialogError);
            }
        }
    };

    useEffect(() => {
        handleSummary();
    }, []);

    const dialogMessage = dialogMessageProps.show && (
        <DialogMessage
            type={dialogMessageProps.type}
            show={dialogMessageProps.show}
            title={dialogMessageProps.title}
            text={dialogMessageProps.text}
            onClose={dialogMessageProps.onClose}
        />
    );

    return (
        <>
            <p style={{ padding: "16px 0" }}>
                Confira abaixo o extrato das horas trabalhadas
            </p>
            <SummaryStyles.StyledTableContent>
                <SummaryStyles.StyledTableContainer>
                    {summary && (
                        <SummaryList
                            summary={summary}
                            handleChangeContent={handleChangeContent}
                        />
                    )}
                    {!readOnly &&
                        summary.resultadoFinal?.saldoFinalHoras !== 0 && (
                            <SummaryStyles.StyledCheckboxArea>
                                <StyledFormCheck
                                    id="checkbox"
                                    type="checkbox"
                                    checked={formSubmit.isChecked}
                                    onChange={() => {
                                        handleChangeCheck();
                                    }}
                                    label={handleLabelCheckbox()}
                                />
                            </SummaryStyles.StyledCheckboxArea>
                        )}
                </SummaryStyles.StyledTableContainer>
            </SummaryStyles.StyledTableContent>
            {!readOnly && summary.resultadoFinal?.saldoFinalHoras !== 0 && (
                <>
                    <p style={{ fontWeight: "bold", marginTop: "40px" }}>
                        Justificativa
                    </p>
                    <InputTextArea
                        placeholder="Insira aqui sua justificativa."
                        value={formSubmit.justification}
                        maxLength={500}
                        onChange={(e: any) =>
                            handleChangeJustification(e.target.value)
                        }
                        style={{ height: "88px" }}
                    />
                </>
            )}
            {!readOnly && (
                <StyledContainerButton>
                    <BtnPrimary
                        style={{ width: 126, marginTop: 24 }}
                        onClick={() => handleSubmit()}
                        disabled={!isEnabledSubmit()}
                    >
                        Concluir
                    </BtnPrimary>
                </StyledContainerButton>
            )}
            {dialogMessage}
        </>
    );
};

export default ActivitiesManagementSummary;
